import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { KindBadge } from "./ui";

// Vista circolare a 360°: tutto ciò che il CRM sa di un contatto (email/telefono).
// hide: sezioni da nascondere, es. ["person"] nella pagina della persona stessa.
export default function Contact360({ email, phone, name, companyId, leadId, hide = [] }) {
  const [d, setD] = useState(null);
  const [story, setStory] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const navigate = useNavigate();
  const h = (k) => hide.includes(k);

  const loadStory = () => {
    if (storyOpen) { setStoryOpen(false); return; }
    setStoryOpen(true);
    if (!story) {
      api.get("/contact360/timeline", { params: { email: email || undefined, phone: phone || undefined, lead_id: leadId || undefined }, noCache: true })
        .then((r) => setStory(r.data)).catch(() => setStory({ events: [], totale: 0 }));
    }
  };

  const load = useCallback(() => {
    if (!email && !phone && !companyId) { setD(null); return; }
    api.get("/contact360", { params: { email: email || undefined, phone: phone || undefined, company_id: companyId || undefined } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [email, phone, companyId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;

  const followLead = async () => {
    try {
      const r = await api.post("/pipeline", {
        email: email || d.person?.email || null,
        phone: phone || d.person?.phone || null,
        name: name || d.person?.name || "",
      });
      navigate(`/lead/${r.data.id}`);
    } catch (e) {
      if (e?.response?.status === 409) { load(); alert("Questo contatto è già nei lead."); }
      else alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }) : "");

  return (
    <div className="panel" data-testid="contact360-panel">
      <div className="toolbar" style={{ marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>👁 Vista completa</h3>
        <div className="spacer" />
        {!d.lead && (email || phone) && !h("lead") && (
          <button className="btn sm primary" onClick={followLead} data-testid="c360-follow-lead-btn">➕ Segui come lead</button>
        )}
      </div>

      <div className="c360-badges">
        {!h("lead") && d.lead && (
          <Link to={`/lead/${d.lead.id}`} className="c360-lead-badge" data-testid="c360-lead-badge">
            🎯 {d.lead.closed ? (d.lead.outcome === "cliente" ? "Ex lead · diventato cliente" : "Lead chiuso · non ha accettato") : "Lead in gestione"} · {d.lead.stage_label} →
          </Link>
        )}
        {!h("person") && d.person && (
          <Link to={`/persone/${d.person.id}`} className="c360-chip" data-testid="c360-person-link">👤 {d.person.name} <KindBadge kind={d.person.kind} /></Link>
        )}
        {!h("companies") && d.companies.map((c) => (
          <Link key={c.id} to={`/aziende/${c.id}`} className="c360-chip" data-testid={`c360-company-${c.id}`}>🏢 {c.name} <KindBadge kind={c.kind} /></Link>
        ))}
        {!d.person && d.companies.length === 0 && !d.lead && (
          <span className="muted" style={{ fontSize: 13 }}>Nessuna anagrafica collegata a questo contatto.</span>
        )}
      </div>
      {!h("lead") && d.lead?.next_action && !d.lead.closed && (
        <p className="c360-next" data-testid="c360-next-action">→ Prossimo passo: {d.lead.next_action}</p>
      )}

      <div className="c360-grid">
        <div className="c360-cell"><div className="c360-num" data-testid="c360-emails-in">{d.emails_in.count}</div><div className="c360-lbl">email ricevute</div></div>
        <div className="c360-cell"><div className="c360-num" data-testid="c360-emails-out">{d.emails_out.count}</div><div className="c360-lbl">email inviate{d.emails_out.last_at ? ` · ult. ${fmt(d.emails_out.last_at)}` : ""}</div></div>
        <div className="c360-cell"><div className="c360-num" data-testid="c360-appts">{d.appointments.count}</div><div className="c360-lbl">appuntamenti</div></div>
        <div className="c360-cell"><div className="c360-num" data-testid="c360-tasks">{d.open_tasks.length}</div><div className="c360-lbl">incarichi aperti</div></div>
      </div>

      {(d.appointments.next || d.appointments.last) && (
        <p style={{ fontSize: 13, marginTop: 10 }} data-testid="c360-appt-info">
          {d.appointments.next && <>📅 Prossimo appuntamento: <strong>{fmt(d.appointments.next.date)} {d.appointments.next.time}</strong>{d.appointments.next.title ? ` · ${d.appointments.next.title}` : ""}</>}
          {d.appointments.next && d.appointments.last && " · "}
          {d.appointments.last && <span className="muted">ultimo svolto: {fmt(d.appointments.last.date)}</span>}
        </p>
      )}

      {!h("emails") && d.emails_in.recent.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="muted c360-title">Ultime email ricevute</div>
          <ul className="c360-list">
            {d.emails_in.recent.map((m) => (
              <li key={m.id}>
                <span>{m.channel === "whatsapp" ? "💬" : "📥"} <Link to={`/email/${m.id}`}>{m.subject}</Link>{m.from ? <span className="muted" style={{ fontSize: 11 }}> — da {m.from}</span> : null}</span>
                <span className="tl-at">{fmt(m.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.open_tasks.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="muted c360-title">Incarichi aperti</div>
          <ul className="c360-list">
            {d.open_tasks.map((t) => (
              <li key={t.id}><span>☐ {t.title}</span>{t.due_date && <span className="tl-at">entro {fmt(t.due_date)}</span>}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button className="btn sm" onClick={loadStory} data-testid="c360-story-btn">
          {storyOpen ? "▲ Nascondi cronistoria" : "📜 Cronistoria completa"}
        </button>
        <button className="btn sm" data-testid="c360-story-pdf-btn" onClick={async () => {
          try {
            const r = await api.get("/contact360/timeline/pdf", {
              params: { email: email || undefined, phone: phone || undefined, nome: name || undefined, lead_id: leadId || undefined },
              responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `cronistoria_${(email || phone || "contatto").replace("@", "_")}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          } catch { alert("Esportazione PDF non riuscita, riprova."); }
        }}>📄 Scarica tutto in PDF</button>
      </div>
      {storyOpen && (
        <div style={{ marginTop: 10 }} data-testid="c360-story">
          {!story ? <p className="muted" style={{ fontSize: 13 }}>Carico la cronistoria…</p> : (
            story.events.length === 0 ? <p className="muted" style={{ fontSize: 13 }}>Nessun evento registrato per questo contatto.</p> : (
              <>
                <div className="muted c360-title">{story.totale} eventi — dal più recente</div>
                <ul className="c360-list" style={{ maxHeight: 420, overflowY: "auto" }}>
                  {story.events.map((e, i) => (
                    <li key={i} data-testid={`c360-story-row-${i}`}>
                      <span style={{ minWidth: 0 }}>
                        {e.icona}{" "}
                        {e.email_id ? <Link to={`/email/${e.email_id}`}>{e.titolo}</Link> : e.titolo}
                        {e.dettaglio && <span className="muted"> · {e.dettaglio}</span>}
                        {e.esito && (
                          <span className="pill" title={e.errore || ""} style={{ marginLeft: 6, background: e.esito === "in attesa di approvazione" ? "#92600a" : "#b91c1c" }}>
                            {e.esito}
                          </span>
                        )}
                      </span>
                      <span className="tl-at" style={{ whiteSpace: "nowrap" }}>
                        {e.at ? new Date(e.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )
          )}
        </div>
      )}
    </div>
  );
}
